import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Store, Building2, Hash, Car, Wrench, Package, ShoppingCart, Phone, RectangleHorizontal,
  ArrowLeft, ArrowRight, Check, Upload, User, Briefcase, FileText,
  Loader2, CheckCircle, Image, File, X, Scissors, Languages, CalendarX, AlertCircle, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// Import custom store forms
import CarStoreForm from '../components/stores/CarStoreForm';
import RealEstateForm from '../components/stores/RealEstateForm';
import { CarAdPreview, RealEstateAdPreview } from '../components/stores/AdPreview';
import PlateDesigner from '../components/stores/PlateDesigner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping from string to component
const iconMap = {
  'ShoppingCart': ShoppingCart,
  'Phone': Phone,
  'Hash': Hash,
  'RectangleHorizontal': RectangleHorizontal,
  'Car': Car,
  'Wrench': Wrench,
  'Building2': Building2,
  'Package': Package,
  'Store': Store
};

// Color mapping from hex to gradient
const getGradientFromColor = (color) => {
  const colorMap = {
    '#06B6D4': 'from-cyan-500 to-blue-500',
    '#1E9BD9': 'from-blue-500 to-cyan-500',
    '#F59E0B': 'from-amber-500 to-yellow-500',
    '#10B981': 'from-green-500 to-emerald-500',
    '#EF4444': 'from-orange-500 to-red-500',
    '#1E9BD9': 'from-[#1E9BD9] to-[#0D7AB5]'
  };
  return colorMap[color] || 'from-gray-500 to-gray-600';
};

// Fallback store types (used if API fails) - synced with ASB606 API
const fallbackStoreTypes = [
  { id: 'ecommerce', name: 'تجارة إلكترونية', nameEn: 'E-Commerce', icon: ShoppingCart, color: 'from-[#1E9BD9] to-cyan-500', desc: 'متجر لبيع المنتجات عبر الإنترنت', descEn: 'Online store for selling products', activityTypes: ['personal', 'company'], sortOrder: 1 },
  { id: 'phone_numbers', name: 'أرقام مميزة', nameEn: 'Phone Numbers', icon: Phone, color: 'from-[#1E9BD9] to-blue-600', desc: 'متجر لبيع الأرقام المميزة', descEn: 'Store for selling premium phone numbers', activityTypes: ['personal', 'company'], sortOrder: 2 },
  { id: 'car_plates', name: 'لوحات السيارات', nameEn: 'Car Plates', icon: RectangleHorizontal, color: 'from-amber-500 to-yellow-500', desc: 'متجر لبيع لوحات السيارات المميزة', descEn: 'Store for selling premium car plates', activityTypes: ['personal', 'company'], sortOrder: 3 },
  { id: 'auto', name: 'السيارات', nameEn: 'Automobiles', icon: Car, color: 'from-green-500 to-emerald-500', desc: 'متجر لبيع السيارات', descEn: 'Store for selling automobiles', activityTypes: ['personal', 'company'], sortOrder: 4 },
  { id: 'real_estate', name: 'العقارات', nameEn: 'Real Estate', icon: Building2, color: 'from-[#1E9BD9] to-blue-500', desc: 'متجر لبيع وتأجير العقارات', descEn: 'Store for selling and renting real estate', activityTypes: ['personal', 'company'], sortOrder: 5 },
  { id: 'spare_parts', name: 'قطع الغيار', nameEn: 'Spare Parts', icon: Wrench, color: 'from-red-500 to-orange-500', desc: 'متجر لبيع قطع الغيار', descEn: 'Store for selling spare parts', activityTypes: ['personal', 'company'], sortOrder: 6 }
];

export default function CreateStore() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // Step state - dynamic based on store type
  const [currentStep, setCurrentStep] = useState(1);
  
  // Check if store type needs custom form
  const needsCustomForm = (type) => ['auto', 'plates', 'car_plates'].includes(type);
  
  // Calculate total steps based on store type
  const getTotalSteps = () => {
    if (needsCustomForm(storeType)) {
      return 5; // Type -> Business -> Custom Form -> Details -> Review
    }
    return 4; // Type -> Business -> Details -> Review
  };
  
  // Store types from API
  const [storeTypes, setStoreTypes] = useState(fallbackStoreTypes);
  const [loadingStoreTypes, setLoadingStoreTypes] = useState(true);
  
  // Form state
  const [storeType, setStoreType] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [isOwner, setIsOwner] = useState(null);
  const [storeName, setStoreName] = useState('');
  const [storeNameEn, setStoreNameEn] = useState('');
  const [storeDescription, setStoreDescription] = useState('');
  const [storeDescriptionEn, setStoreDescriptionEn] = useState('');
  
  // Custom form data (for auto, real_estate, plates)
  const [customFormData, setCustomFormData] = useState({});
  
  // Car images for preview
  const [carImages, setCarImages] = useState([]);
  
  // File states
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedDefaultLogo, setSelectedDefaultLogo] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [licensePreview, setLicensePreview] = useState(null);
  const [authorizationFile, setAuthorizationFile] = useState(null);
  const [authorizationPreview, setAuthorizationPreview] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  
  // License analysis states
  const [analyzingLicense, setAnalyzingLicense] = useState(false);
  const [licenseData, setLicenseData] = useState(null);
  const [licenseRejected, setLicenseRejected] = useState(false);
  const [licenseRejectionReason, setLicenseRejectionReason] = useState('');
  const [licenseExpired, setLicenseExpired] = useState(false);
  const [licenseExpiryDate, setLicenseExpiryDate] = useState('');
  
  // Image cropper states
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const cropImageRef = useRef(null);
  
  // Refs
  const logoInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  const authorizationInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  
  // Fetch store types from ASB606 Admin Platform on component mount
  useEffect(() => {
    const fetchStoreTypes = async () => {
      try {
        setLoadingStoreTypes(true);
        // Fetch from ASB606 Admin Platform API
        const response = await axios.get('https://asb606.com/api/store-types/active');
        if (response.data.success && response.data.data) {
          const apiTypes = response.data.data.map(type => ({
            id: type.id,
            name: type.name_ar,
            nameEn: type.name_en,
            icon: iconMap[type.icon] || Store,
            color: getGradientFromColor(type.color),
            desc: type.description_ar || type.desc_ar || type.description || '',
            descEn: type.description_en || type.desc_en || type.descriptionEn || '',
            activityTypes: type.activity_types || ['personal', 'company'],
            sortOrder: type.sort_order || 99
          }));
          // Sort by sort_order
          apiTypes.sort((a, b) => a.sortOrder - b.sortOrder);
          setStoreTypes(apiTypes);
        }
      } catch (error) {
        console.error('Failed to fetch store types from ASB606:', error);
        // Fallback to default types on error
        setStoreTypes(fallbackStoreTypes);
      } finally {
        setLoadingStoreTypes(false);
      }
    };
    
    fetchStoreTypes();
  }, []);
  
  // Check if store type is available for current business type
  const isStoreTypeAvailable = useCallback((type) => {
    if (!businessType) return true; // Show all if no business type selected
    return type.activityTypes?.includes(businessType) ?? true;
  }, [businessType]);
  
  // Reset store type when business type changes and current selection is not available
  useEffect(() => {
    if (storeType && businessType) {
      const selectedType = storeTypes.find(t => t.id === storeType);
      if (selectedType && !selectedType.activityTypes?.includes(businessType)) {
        setStoreType('');
        toast.warning(
          language === 'ar' 
            ? 'تم إلغاء اختيار نوع المتجر لأنه غير متاح لنوع النشاط المحدد'
            : 'Store type selection cleared as it is not available for the selected activity type'
        );
      }
    }
  }, [businessType, storeType, storeTypes, language]);

  // Default logos
  const defaultLogos = [
    // عقارات - Real Estate (أزرق سماوي)
    { id: 'real_estate_1', category: 'real_estate', color: 'from-sky-400 to-blue-600', icon: '🏠' },
    { id: 'real_estate_2', category: 'real_estate', color: 'from-blue-400 to-indigo-600', icon: '🏢' },
    { id: 'real_estate_3', category: 'real_estate', color: 'from-cyan-400 to-blue-500', icon: '🏗️' },
    { id: 'real_estate_4', category: 'real_estate', color: 'from-teal-400 to-cyan-600', icon: '🏘️' },
    { id: 'real_estate_5', category: 'real_estate', color: 'from-blue-500 to-sky-400', icon: '🏡' },
    { id: 'real_estate_6', category: 'real_estate', color: 'from-indigo-400 to-blue-500', icon: '🌆' },
    { id: 'real_estate_7', category: 'real_estate', color: 'from-sky-500 to-cyan-400', icon: '🔑' },
    { id: 'real_estate_8', category: 'real_estate', color: 'from-blue-600 to-indigo-500', icon: '🏛️' },
    
    // أرقام الهواتف - Phone Numbers (أزرق)
    { id: 'phone_1', category: 'phone_numbers', color: 'from-[#1E9BD9] to-[#0D7AB5]', icon: '📱' },
    { id: 'phone_2', category: 'phone_numbers', color: 'from-[#1E9BD9] to-[#065A82]', icon: '📞' },
    { id: 'phone_3', category: 'phone_numbers', color: 'from-blue-500 to-[#1E9BD9]', icon: '☎️' },
    { id: 'phone_4', category: 'phone_numbers', color: 'from-[#1E9BD9] to-[#0D7AB5]', icon: '🔢' },
    { id: 'phone_5', category: 'phone_numbers', color: 'from-cyan-500 to-blue-600', icon: '💎' },
    { id: 'phone_6', category: 'phone_numbers', color: 'from-[#1E9BD9] to-[#0D7AB5]', icon: '⭐' },
    { id: 'phone_7', category: 'phone_numbers', color: 'from-[#065A82] to-[#1E9BD9]', icon: '🌟' },
    { id: 'phone_8', category: 'phone_numbers', color: 'from-blue-600 to-cyan-500', icon: '✨' },
    
    // أرقام السيارات - Car Plates (ذهبي/أصفر)
    { id: 'plates_1', category: 'car_plates', color: 'from-yellow-400 to-amber-600', icon: '🔢' },
    { id: 'plates_2', category: 'car_plates', color: 'from-amber-400 to-yellow-600', icon: '🏷️' },
    { id: 'plates_3', category: 'car_plates', color: 'from-orange-400 to-amber-500', icon: '🚘' },
    { id: 'plates_4', category: 'car_plates', color: 'from-yellow-500 to-orange-500', icon: '🪪' },
    { id: 'plates_5', category: 'car_plates', color: 'from-amber-500 to-yellow-400', icon: '⭐' },
    { id: 'plates_6', category: 'car_plates', color: 'from-yellow-600 to-amber-400', icon: '🏅' },
    { id: 'plates_7', category: 'car_plates', color: 'from-amber-600 to-orange-400', icon: '👑' },
    { id: 'plates_8', category: 'car_plates', color: 'from-orange-500 to-yellow-500', icon: '💫' },
    
    // قطع غيار - Spare Parts (أحمر/برتقالي)
    { id: 'parts_1', category: 'spare_parts', color: 'from-red-500 to-orange-600', icon: '🔧' },
    { id: 'parts_2', category: 'spare_parts', color: 'from-orange-500 to-red-600', icon: '⚙️' },
    { id: 'parts_3', category: 'spare_parts', color: 'from-red-500 to-red-600', icon: '🛠️' },
    { id: 'parts_4', category: 'spare_parts', color: 'from-red-600 to-red-500', icon: '🔩' },
    { id: 'parts_5', category: 'spare_parts', color: 'from-orange-600 to-red-500', icon: '⛽' },
    { id: 'parts_6', category: 'spare_parts', color: 'from-red-500 to-red-600', icon: '🚗' },
    { id: 'parts_7', category: 'spare_parts', color: 'from-red-600 to-orange-500', icon: '🏎️' },
    { id: 'parts_8', category: 'spare_parts', color: 'from-orange-600 to-red-500', icon: '🛞' },
    
    // تاجر سيارات - Auto Dealer (أخضر)
    { id: 'auto_1', category: 'auto', color: 'from-green-500 to-emerald-600', icon: '🚗' },
    { id: 'auto_2', category: 'auto', color: 'from-emerald-500 to-green-600', icon: '🚙' },
    { id: 'auto_3', category: 'auto', color: 'from-teal-500 to-emerald-600', icon: '🚘' },
    { id: 'auto_4', category: 'auto', color: 'from-green-600 to-teal-500', icon: '🏎️' },
    { id: 'auto_5', category: 'auto', color: 'from-emerald-600 to-green-500', icon: '🚐' },
    { id: 'auto_6', category: 'auto', color: 'from-green-500 to-teal-600', icon: '🚕' },
    { id: 'auto_7', category: 'auto', color: 'from-teal-600 to-green-500', icon: '🛻' },
    { id: 'auto_8', category: 'auto', color: 'from-emerald-500 to-teal-500', icon: '🚎' },
    
    // تجارة إلكترونية - E-Commerce (وردي/فوشيا)
    { id: 'ecom_1', category: 'ecommerce', color: 'from-cyan-500 to-blue-600', icon: '🛒' },
    { id: 'ecom_2', category: 'ecommerce', color: 'from-blue-500 to-cyan-600', icon: '🛍️' },
    { id: 'ecom_3', category: 'ecommerce', color: 'from-teal-500 to-cyan-600', icon: '👗' },
    { id: 'ecom_4', category: 'ecommerce', color: 'from-cyan-600 to-teal-500', icon: '💄' },
    { id: 'ecom_5', category: 'ecommerce', color: 'from-blue-600 to-teal-500', icon: '👟' },
    { id: 'ecom_6', category: 'ecommerce', color: 'from-cyan-500 to-teal-600', icon: '📦' },
    { id: 'ecom_7', category: 'ecommerce', color: 'from-teal-600 to-blue-500', icon: '🎁' },
    { id: 'ecom_8', category: 'ecommerce', color: 'from-blue-500 to-cyan-500', icon: '💍' },
    
    // أيقونات عامة - General (رمادي/أسود)
    { id: 'general_1', category: 'all', color: 'from-slate-500 to-gray-700', icon: '🏪' },
    { id: 'general_2', category: 'all', color: 'from-gray-500 to-slate-700', icon: '🏬' },
    { id: 'general_3', category: 'all', color: 'from-zinc-500 to-gray-700', icon: '💼' },
    { id: 'general_4', category: 'all', color: 'from-neutral-500 to-zinc-700', icon: '🎯' },
  ];
  
  // Filter logos based on selected store type
  const getFilteredLogos = () => {
    if (!storeType) return defaultLogos.filter(l => l.category === 'all');
    return defaultLogos.filter(l => l.category === storeType || l.category === 'all');
  };

  // File Preview Component - supports both images and PDFs
  const FilePreview = ({ file, preview, onRemove, colorClass = "text-gray-600" }) => {
    const isPDF = file?.type === 'application/pdf' || file?.name?.toLowerCase().endsWith('.pdf');
    
    if (isPDF) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center p-3 relative group">
          <div className={`w-16 h-16 rounded-xl bg-red-100 flex items-center justify-center mb-2`}>
            <File className="h-8 w-8 text-red-600" />
          </div>
          <p className="text-sm font-medium text-gray-700 text-center truncate max-w-full px-2">
            {file?.name || 'document.pdf'}
          </p>
          <span className="text-xs text-red-600 font-bold mt-1">PDF</span>
          {onRemove && (
            <button 
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="absolute top-2 left-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    }
    
    // Image preview
    return (
      <div className="relative w-full h-full group">
        <img src={preview} alt="Preview" className="w-full h-full object-contain p-2" />
        {onRemove && (
          <button 
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="absolute top-2 left-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };
  
  // Loading state
  const [creating, setCreating] = useState(false);
  
  // Translation states
  const [translatingName, setTranslatingName] = useState(false);
  const [translatingDesc, setTranslatingDesc] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const translateTimeoutRef = useRef(null);
  
  // Translate text function
  const translateText = async (text, sourceLang, targetLang) => {
    if (!text || !text.trim()) return '';
    
    try {
      const formData = new FormData();
      formData.append('text', text);
      formData.append('source_lang', sourceLang);
      formData.append('target_lang', targetLang);
      
      const response = await axios.post(`${BACKEND_URL}/api/translate`, formData);
      return response.data.translated || '';
    } catch (error) {
      console.error('Translation error:', error);
      return '';
    }
  };
  
  // Auto-translate store name (Arabic to English)
  const handleStoreNameChange = async (value) => {
    setStoreName(value);
    
    // Clear previous timeout
    if (translateTimeoutRef.current) {
      clearTimeout(translateTimeoutRef.current);
    }
    
    // Only translate if English field is empty or was auto-translated
    if (value && value.trim()) {
      translateTimeoutRef.current = setTimeout(async () => {
        setTranslatingName(true);
        const translated = await translateText(value, 'ar', 'en');
        if (translated) {
          setStoreNameEn(translated);
        }
        setTranslatingName(false);
      }, 800); // Wait 800ms after user stops typing
    }
  };
  
  // Auto-translate store name (English to Arabic)
  const handleStoreNameEnChange = async (value) => {
    setStoreNameEn(value);
    
    if (translateTimeoutRef.current) {
      clearTimeout(translateTimeoutRef.current);
    }
    
    if (value && value.trim()) {
      translateTimeoutRef.current = setTimeout(async () => {
        setTranslatingName(true);
        const translated = await translateText(value, 'en', 'ar');
        if (translated) {
          setStoreName(translated);
        }
        setTranslatingName(false);
      }, 800);
    }
  };
  
  // Auto-translate description (Arabic to English)
  const handleDescriptionChange = async (value) => {
    setStoreDescription(value);
    
    if (translateTimeoutRef.current) {
      clearTimeout(translateTimeoutRef.current);
    }
    
    if (value && value.trim()) {
      translateTimeoutRef.current = setTimeout(async () => {
        setTranslatingDesc(true);
        const translated = await translateText(value, 'ar', 'en');
        if (translated) {
          setStoreDescriptionEn(translated);
        }
        setTranslatingDesc(false);
      }, 800);
    }
  };
  
  // Auto-translate description (English to Arabic)
  const handleDescriptionEnChange = async (value) => {
    setStoreDescriptionEn(value);
    
    if (translateTimeoutRef.current) {
      clearTimeout(translateTimeoutRef.current);
    }
    
    if (value && value.trim()) {
      translateTimeoutRef.current = setTimeout(async () => {
        setTranslatingDesc(true);
        const translated = await translateText(value, 'en', 'ar');
        if (translated) {
          setStoreDescription(translated);
        }
        setTranslatingDesc(false);
      }, 800);
    }
  };
  
  // Generate AI description based on store name and type
  const generateDescription = async () => {
    if (!storeName || !storeNameEn) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم المتجر أولاً' : 'Please enter store name first');
      return;
    }
    
    setGeneratingDesc(true);
    
    try {
      const selectedType = storeTypes.find(t => t.id === storeType);
      const storeTypeName = language === 'ar' ? selectedType?.name : selectedType?.nameEn;
      
      const formData = new FormData();
      formData.append('store_name_ar', storeName);
      formData.append('store_name_en', storeNameEn);
      formData.append('store_type', storeTypeName || '');
      
      const response = await axios.post(`${BACKEND_URL}/api/generate-description`, formData);
      
      if (response.data.description_ar) {
        setStoreDescription(response.data.description_ar);
      }
      if (response.data.description_en) {
        setStoreDescriptionEn(response.data.description_en);
      }
      
      toast.success(language === 'ar' ? 'تم إنشاء الوصف بنجاح!' : 'Description generated successfully!');
    } catch (error) {
      console.error('Description generation error:', error);
      toast.error(language === 'ar' ? 'فشل إنشاء الوصف' : 'Failed to generate description');
    } finally {
      setGeneratingDesc(false);
    }
  };

  // File handlers
  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 3; // Max 3MB to avoid MongoDB 16MB limit (Base64 adds ~33%)
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(language === 'ar' ? `حجم الملف يجب أن يكون أقل من ${maxSize} ميجابايت` : `File size must be less than ${maxSize}MB`);
      return;
    }
    
    // For logo, open cropper
    if (type === 'logo' && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImage(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'logo') {
        setLogoFile(file);
        setLogoPreview(reader.result);
      } else if (type === 'license') {
        // Reset rejection state when uploading new file
        setLicenseRejected(false);
        setLicenseRejectionReason('');
        setLicenseExpired(false);
        setLicenseExpiryDate('');
        setLicenseData(null);
        
        setLicenseFile(file);
        setLicensePreview(reader.result);
        // Auto-analyze license if it's an image or PDF
        if (file.type.startsWith('image/') || file.type === 'application/pdf') {
          analyzeLicense(file);
        }
      } else if (type === 'authorization') {
        setAuthorizationFile(file);
        setAuthorizationPreview(reader.result);
      } else if (type === 'signature') {
        setSignatureFile(file);
        setSignaturePreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Analyze license using AI
  const analyzeLicense = async (file) => {
    const token = localStorage.getItem('business_token');
    if (!token) {
      console.error('No token found');
      return;
    }

    console.log('Starting license analysis...', file.name, file.type);
    setAnalyzingLicense(true);
    setLicenseData(null);
    setLicenseRejected(false);
    setLicenseRejectionReason('');
    setLicenseExpired(false);
    setLicenseExpiryDate('');

    try {
      const formData = new FormData();
      formData.append('license_file', file);

      console.log('Sending request to:', `${BACKEND_URL}/api/analyze-license`);
      const response = await axios.post(`${BACKEND_URL}/api/analyze-license`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('API Response:', response.data);

      if (response.data.status === 'rejected') {
        // License rejected - check if it's expired
        console.log('License REJECTED:', response.data.message);
        setLicenseRejected(true);
        setLicenseRejectionReason(response.data.message);
        
        // Check if rejection is due to expiry
        if (response.data.expired) {
          setLicenseExpired(true);
          setLicenseExpiryDate(response.data.expiry_date || '');
        }
        
        // Don't clear the file - let user see what they uploaded and try again
        toast.error(response.data.message);
      } else if (response.data.status === 'success' && response.data.data) {
        // Valid license - auto-fill store name (locked for companies)
        const data = response.data.data;
        console.log('License VALID:', data);
        setLicenseData(data);
        
        // Auto-fill store name from license (cannot be changed for companies)
        if (data.company_name_ar) {
          console.log('Setting Arabic name:', data.company_name_ar);
          setStoreName(data.company_name_ar);
        }
        if (data.company_name_en) {
          console.log('Setting English name:', data.company_name_en);
          setStoreNameEn(data.company_name_en);
        }
        
        toast.success(language === 'ar' ? 'تم التحقق من الرخصة التجارية بنجاح!' : 'Trade license verified successfully!');
      }
    } catch (error) {
      console.error('License analysis error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(language === 'ar' ? 'فشل في تحليل الرخصة' : 'Failed to analyze license');
    } finally {
      setAnalyzingLicense(false);
    }
  };

  // Crop image and save
  const getCroppedImg = useCallback(() => {
    if (!cropImageRef.current) return;
    
    const canvas = document.createElement('canvas');
    const img = cropImageRef.current;
    
    let sourceX, sourceY, sourceWidth, sourceHeight;
    
    if (completedCrop && completedCrop.width && completedCrop.height) {
      // Use the selected crop area
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      sourceX = completedCrop.x * scaleX;
      sourceY = completedCrop.y * scaleY;
      sourceWidth = completedCrop.width * scaleX;
      sourceHeight = completedCrop.height * scaleY;
    } else {
      // Center crop to square if no selection
      const size = Math.min(img.naturalWidth, img.naturalHeight);
      sourceX = (img.naturalWidth - size) / 2;
      sourceY = (img.naturalHeight - size) / 2;
      sourceWidth = size;
      sourceHeight = size;
    }
    
    // Set canvas size
    canvas.width = sourceWidth;
    canvas.height = sourceHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, sourceWidth, sourceHeight);
    
    // Get the data URL directly instead of using File constructor
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    
    // Convert data URL to blob for file upload
    fetch(dataUrl)
      .then(res => res.blob())
      .then(blob => {
        // Create a simple object that works like a file
        const file = new Blob([blob], { type: 'image/jpeg' });
        file.name = 'logo.jpg';
        setLogoFile(file);
        setLogoPreview(dataUrl);
        setShowCropper(false);
        setCropImage(null);
        setSelectedDefaultLogo(null);
      });
  }, [completedCrop]);

  // Cancel cropping
  const cancelCrop = () => {
    setShowCropper(false);
    setCropImage(null);
  };

  // Validation for each step
  const canProceed = () => {
    const totalSteps = getTotalSteps();
    const hasCustomForm = needsCustomForm(storeType);
    
    switch (currentStep) {
      case 1:
        return storeType !== '';
      case 2:
        if (!businessType) return false;
        if (businessType === 'company') {
          if (!licenseFile) return false;
          if (isOwner === null) return false;
          if (isOwner === false && (!authorizationFile || !signatureFile)) return false;
        }
        return true;
      case 3:
        // If has custom form, step 3 is custom form
        if (hasCustomForm) {
          // Basic validation for custom forms
          if (storeType === 'auto') {
            return customFormData.make && customFormData.model && customFormData.price;
          }
          if (storeType === 'real_estate') {
            return customFormData.propertyType && customFormData.transactionType && customFormData.price;
          }
          if (storeType === 'plates' || storeType === 'car_plates') {
            return customFormData.category && customFormData.number && customFormData.price;
          }
          return true;
        }
        // Otherwise step 3 is store details
        return storeName !== '' && storeNameEn !== '';
      case 4:
        // If has custom form, step 4 is store details
        if (hasCustomForm) {
          return storeName !== '' && storeNameEn !== '';
        }
        // Otherwise step 4 is review
        return true;
      case 5:
        // Step 5 only exists for custom forms (review)
        return true;
      default:
        return false;
    }
  };

  // Handle step navigation
  const nextStep = () => {
    const totalSteps = getTotalSteps();
    if (canProceed() && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Submit store
  const handleSubmit = async () => {
    const token = localStorage.getItem('business_token');
    
    // Check if token exists
    if (!token) {
      toast.error(language === 'ar' ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login');
      return;
    }
    
    setCreating(true);

    try {
      const formData = new FormData();
      formData.append('name', storeName);
      formData.append('nameEn', storeNameEn);
      formData.append('type', storeType);
      formData.append('description', storeDescription);
      formData.append('descriptionEn', storeDescriptionEn);
      formData.append('businessType', businessType);
      
      if (logoFile) {
        formData.append('logo', logoFile);
      } else if (selectedDefaultLogo) {
        formData.append('defaultLogo', selectedDefaultLogo);
      }
      
      if (businessType === 'company') {
        formData.append('isOwner', isOwner);
        if (licenseFile) {
          formData.append('license', licenseFile);
        }
        // Add license data from AI analysis
        if (licenseData) {
          formData.append('licenseData', JSON.stringify(licenseData));
        }
        if (isOwner === false) {
          if (authorizationFile) {
            formData.append('authorization', authorizationFile);
          }
          if (signatureFile) {
            formData.append('signatureApproval', signatureFile);
          }
        }
      }

      await axios.post(`${BACKEND_URL}/api/stores`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(language === 'ar' ? 'تم إرسال طلب إنشاء المتجر! سيتم مراجعته خلال 48 ساعة كحد أقصى.' : 'Store request submitted! It will be reviewed within 48 hours maximum.');
      navigate('/stores');
    } catch (error) {
      // Handle token expiration
      if (error.response?.status === 401) {
        toast.error(language === 'ar' ? 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى' : 'Session expired. Please login again');
        localStorage.removeItem('business_token');
        localStorage.removeItem('business_user');
        navigate('/login');
        return;
      }
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل إنشاء المتجر' : 'Failed to create store'));
    } finally {
      setCreating(false);
    }
  };

  // Get selected store type info
  const selectedStoreType = storeTypes.find(t => t.id === storeType);

  // Step indicator - dynamic based on store type
  const StepIndicator = () => {
    const totalSteps = getTotalSteps();
    const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);
    
    return (
      <div className="flex items-center justify-center mb-6">
        {steps.map((step, index) => (
          <div key={step} className="flex items-center">
            <div 
              className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                step < currentStep 
                  ? 'gradient-primary text-white shadow-lg shadow-[#38BDF8]/30' 
                  : step === currentStep 
                  ? 'gradient-primary text-white scale-110 shadow-lg shadow-[#F97316]/30 animate-pulse-glow' 
                  : isDarkMode ? 'bg-white/10 text-gray-400 border border-white/10' : 'bg-gray-100 text-gray-500 border border-gray-200'
              }`}
            >
              {step < currentStep ? <Check className="h-4 w-4" /> : step}
            </div>
            {index < steps.length - 1 && (
              <div className={`w-10 sm:w-16 h-1 mx-1.5 rounded-full transition-all duration-300 ${
                step < currentStep 
                  ? 'gradient-primary' 
                  : isDarkMode ? 'bg-white/10' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Get step title based on current step and store type
  const getStepTitle = () => {
    const hasCustomForm = needsCustomForm(storeType);
    
    if (hasCustomForm) {
      // 5 steps: Type -> Business -> Custom Form -> Details -> Review
      const step3Title = storeType === 'auto' 
        ? { ar: 'بيانات السيارة', en: 'Car Details' }
        : storeType === 'real_estate'
          ? { ar: 'بيانات العقار', en: 'Property Details' }
          : { ar: 'بيانات اللوحة', en: 'Plate Details' };
      
      const titles = {
        1: { ar: 'اختر نوع المتجر', en: 'Choose Store Type' },
        2: { ar: 'نوع النشاط التجاري', en: 'Business Type' },
        3: step3Title,
        4: { ar: 'تفاصيل المتجر', en: 'Store Details' },
        5: { ar: 'مراجعة وتأكيد', en: 'Review & Confirm' }
      };
      return titles[currentStep] || { ar: '', en: '' };
    } else {
      // 4 steps: Type -> Business -> Details -> Review
      const titles = {
        1: { ar: 'اختر نوع المتجر', en: 'Choose Store Type' },
        2: { ar: 'نوع النشاط التجاري', en: 'Business Type' },
        3: { ar: 'تفاصيل المتجر', en: 'Store Details' },
        4: { ar: 'مراجعة وتأكيد', en: 'Review & Confirm' }
      };
      return titles[currentStep] || { ar: '', en: '' };
    }
  };

  // Step titles (kept for backward compatibility)
  const stepTitles = {
    1: { ar: 'اختر نوع المتجر', en: 'Choose Store Type' },
    2: { ar: 'نوع النشاط التجاري', en: 'Business Type' },
    3: { ar: 'تفاصيل المتجر', en: 'Store Details' },
    4: { ar: 'مراجعة وتأكيد', en: 'Review & Confirm' },
    5: { ar: 'مراجعة وتأكيد', en: 'Review & Confirm' }
  };

  return (
    <div className={`min-h-screen py-6 cgm-page`}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute top-20 start-10 w-[300px] h-[300px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#38BDF8]/10' : 'bg-[#38BDF8]/5'}`} />
        <div className={`absolute bottom-20 end-10 w-[250px] h-[250px] rounded-full blur-[80px] ${isDarkMode ? 'bg-[#F97316]/10' : 'bg-[#F97316]/5'}`} />
      </div>
      
      <div className="relative max-w-3xl mx-auto px-4">
        {/* Header - Compact Premium */}
        <div className={`flex items-center justify-between mb-4 p-3 rounded-xl ${
          isDarkMode ? 'bg-[#131B2C]/80 border border-white/10' : 'bg-white/80 backdrop-blur-xl border border-black/5 shadow-sm'
        }`}>
          <button 
            onClick={() => navigate('/stores')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
              isDarkMode 
                ? 'text-gray-400 hover:text-[#38BDF8] hover:bg-white/5' 
                : 'text-gray-600 hover:text-[#38BDF8] hover:bg-gray-50'
            }`}
          >
            <ArrowLeft className="h-5 w-5 rtl:rotate-180" />
            <span className="text-sm">{language === 'ar' ? 'العودة للمتاجر' : 'Back to Stores'}</span>
          </button>
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            <span className="text-gradient">{language === 'ar' ? 'إنشاء متجر جديد' : 'Create New Store'}</span>
          </h1>
        </div>

        {/* Step Indicator */}
        <StepIndicator />

        {/* Step Title */}
        <h2 className={`text-xl sm:text-2xl font-bold text-center mb-6 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
          {language === 'ar' ? getStepTitle().ar : getStepTitle().en}
        </h2>

        {/* Step Content - Premium Card */}
        <div className={`rounded-2xl shadow-lg p-5 sm:p-6 mb-5 border transition-all duration-300 ${
          isDarkMode 
            ? 'bg-[#131B2C]/80 backdrop-blur-xl border-white/10' 
            : 'bg-white/80 backdrop-blur-xl border-black/5'
        }`}>
          
          {/* Step 1: Store Type */}
          {currentStep === 1 && (
            <div className="space-y-4">
              {loadingStoreTypes ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" />
                  <span className={`ms-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{language === 'ar' ? 'جاري تحميل أنواع المتاجر...' : 'Loading store types...'}</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {storeTypes.map((type) => {
                    const Icon = type.icon;
                    const isCompanyOnly = type.activityTypes?.length === 1 && type.activityTypes[0] === 'company';
                    return (
                      <button
                        key={type.id}
                        onClick={() => setStoreType(type.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-start relative group ${
                          storeType === type.id
                            ? 'border-[#38BDF8] bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10 shadow-md'
                            : isDarkMode
                              ? 'border-white/10 hover:border-[#38BDF8]/50 hover:bg-white/5'
                              : 'border-gray-200 hover:border-[#38BDF8]/50 hover:bg-gray-50'
                        }`}
                        data-testid={`store-type-${type.id}`}
                      >
                        {/* Company Only Badge */}
                        {isCompanyOnly && (
                          <div className="absolute top-2 end-2 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                            <Briefcase className="h-3 w-3" />
                            {language === 'ar' ? 'شركات فقط' : 'Companies Only'}
                          </div>
                        )}
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${type.color} flex items-center justify-center mb-2 group-hover:scale-105 transition-transform`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className={`font-bold text-base mb-1 ${storeType === type.id ? 'text-gradient' : isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {language === 'ar' ? type.name : type.nameEn}
                        </h3>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? type.desc : type.descEn}
                        </p>
                        {storeType === type.id && (
                          <div className="mt-2 flex items-center gap-1 text-[#38BDF8]">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-xs font-medium">{language === 'ar' ? 'تم الاختيار' : 'Selected'}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Business Type */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Check if store type is company-only */}
              {(() => {
                const selectedType = storeTypes.find(t => t.id === storeType);
                const isCompanyOnlyType = selectedType?.activityTypes?.length === 1 && selectedType?.activityTypes[0] === 'company';
                const isPersonalDisabled = isCompanyOnlyType;
                
                return (
                  <>
                    {/* Warning for company-only store types */}
                    {isCompanyOnlyType && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-800 font-medium">
                            {language === 'ar' 
                              ? `نوع المتجر "${selectedType?.name}" متاح للشركات فقط`
                              : `Store type "${selectedType?.nameEn}" is available for companies only`
                            }
                          </p>
                          <p className="text-amber-600 text-sm mt-1">
                            {language === 'ar' 
                              ? 'يجب أن يكون لديك رخصة تجارية لإنشاء هذا النوع من المتاجر'
                              : 'You must have a commercial license to create this type of store'
                            }
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Business Type Selection */}
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => {
                          if (!isPersonalDisabled) {
                            setBusinessType('personal');
                            setIsOwner(null);
                            setLicenseFile(null);
                            setLicensePreview(null);
                            setAuthorizationFile(null);
                            setAuthorizationPreview(null);
                          }
                        }}
                        disabled={isPersonalDisabled}
                        className={`p-6 rounded-xl border-2 transition-all text-center relative ${
                          isPersonalDisabled
                            ? isDarkMode 
                              ? 'border-white/10 bg-white/5 opacity-60 cursor-not-allowed'
                              : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                            : businessType === 'personal'
                              ? isDarkMode 
                                ? 'border-[#38BDF8] bg-[#38BDF8]/10'
                                : 'border-[#1E9BD9] bg-[#1E9BD9]/5'
                              : isDarkMode
                                ? 'border-white/10 hover:border-white/20'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="business-type-personal"
                      >
                        {isPersonalDisabled && (
                          <div className={`absolute top-2 end-2 px-2 py-0.5 rounded text-xs ${
                            isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {language === 'ar' ? 'غير متاح' : 'Not Available'}
                          </div>
                        )}
                        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${
                          businessType === 'personal' && !isPersonalDisabled 
                            ? isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#1E9BD9]/20' 
                            : isDarkMode ? 'bg-white/10' : 'bg-gray-100'
                        }`}>
                          <User className={`h-8 w-8 ${
                            businessType === 'personal' && !isPersonalDisabled 
                              ? isDarkMode ? 'text-[#38BDF8]' : 'text-[#1E9BD9]' 
                              : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`} />
                        </div>
                        <h3 className={`font-bold text-lg ${
                          businessType === 'personal' && !isPersonalDisabled 
                            ? isDarkMode ? 'text-[#38BDF8]' : 'text-[#1E9BD9]' 
                            : isDarkMode ? 'text-white' : 'text-[#0F172A]'
                        }`}>
                          {language === 'ar' ? 'شخصي' : 'Personal'}
                        </h3>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'لا تتطلب مستندات إضافية' : 'No additional documents required'}
                        </p>
                      </button>

                      <button
                        onClick={() => setBusinessType('company')}
                        className={`p-6 rounded-xl border-2 transition-all text-center ${
                          businessType === 'company'
                            ? isDarkMode 
                              ? 'border-[#38BDF8] bg-[#38BDF8]/10'
                              : 'border-[#1E9BD9] bg-[#1E9BD9]/5'
                            : isDarkMode
                              ? 'border-white/10 hover:border-white/20'
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="business-type-company"
                      >
                        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${
                          businessType === 'company' 
                            ? isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#1E9BD9]/20' 
                            : isDarkMode ? 'bg-white/10' : 'bg-gray-100'
                        }`}>
                          <Briefcase className={`h-8 w-8 ${
                            businessType === 'company' 
                              ? isDarkMode ? 'text-[#38BDF8]' : 'text-[#1E9BD9]' 
                              : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`} />
                        </div>
                        <h3 className={`font-bold text-lg ${
                          businessType === 'company' 
                            ? isDarkMode ? 'text-[#38BDF8]' : 'text-[#1E9BD9]' 
                            : isDarkMode ? 'text-white' : 'text-[#0F172A]'
                        }`}>
                          {language === 'ar' ? 'شركة' : 'Company'}
                        </h3>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'تتطلب رخصة تجارية' : 'Requires commercial license'}
                        </p>
                      </button>
                    </div>
                  </>
                );
              })()}

              {/* Company Documents */}
              {businessType === 'company' && (
                <div className="space-y-4 mt-6">
                  {/* Commercial License */}
                  <div className={`rounded-xl p-5 border ${
                    isDarkMode 
                      ? 'bg-orange-500/10 border-orange-500/30' 
                      : 'bg-orange-50 border-orange-200'
                  }`}>
                    <Label className={`font-bold mb-3 flex items-center gap-2 ${
                      isDarkMode ? 'text-orange-400' : 'text-gray-800'
                    }`}>
                      <FileText className={`h-5 w-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                      {language === 'ar' ? 'الرخصة التجارية *' : 'Commercial License *'}
                    </Label>
                    <div 
                      onClick={() => licenseInputRef.current?.click()}
                      className={`w-full h-36 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors overflow-hidden ${
                        isDarkMode 
                          ? 'border-orange-500/40 hover:border-orange-500/60 bg-[#0D1421]' 
                          : 'border-orange-300 hover:border-orange-400 bg-white'
                      }`}
                    >
                      {licenseFile ? (
                        <FilePreview 
                          file={licenseFile} 
                          preview={licensePreview}
                          onRemove={() => { setLicenseFile(null); setLicensePreview(null); }}
                        />
                      ) : (
                        <div className="text-center">
                          <Upload className={`h-10 w-10 mx-auto ${isDarkMode ? 'text-orange-400' : 'text-orange-400'}`} />
                          <p className={`text-sm mt-2 font-medium ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                            {language === 'ar' ? 'اضغط لرفع صورة الرخصة' : 'Click to upload license'}
                          </p>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-orange-400/60' : 'text-orange-400'}`}>
                            PNG, JPG, PDF - {language === 'ar' ? '3 ميجابايت كحد أقصى' : '3MB max'}
                          </p>
                        </div>
                      )}
                    </div>
                    <input ref={licenseInputRef} type="file" accept="image/*,.pdf" onChange={(e) => handleFileUpload(e, 'license')} className="hidden" />
                    
                    {/* AI License Analysis Status */}
                    {analyzingLicense && (
                      <div className="mt-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex items-center gap-3">
                          <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                          <span className="text-blue-700 font-medium">
                            {language === 'ar' ? 'جاري التحقق من الرخصة التجارية...' : 'Verifying trade license...'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* License Rejected */}
                    {licenseRejected && (
                      <div className={`mt-3 p-4 rounded-xl border ${licenseExpired ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-center gap-3">
                          {licenseExpired ? (
                            <CalendarX className="h-6 w-6 text-amber-600" />
                          ) : (
                            <X className="h-5 w-5 text-red-500" />
                          )}
                          <div className="flex-1">
                            <span className={`font-bold block ${licenseExpired ? 'text-amber-800' : 'text-red-700'}`}>
                              {licenseExpired 
                                ? (language === 'ar' ? 'الرخصة التجارية منتهية الصلاحية' : 'Trade License Expired')
                                : (language === 'ar' ? 'تم رفض الملف' : 'File Rejected')
                              }
                            </span>
                            {licenseExpired && licenseExpiryDate && (
                              <span className="text-amber-700 text-sm block mt-1">
                                {language === 'ar' 
                                  ? `تاريخ الانتهاء: ${licenseExpiryDate}` 
                                  : `Expiry Date: ${licenseExpiryDate}`
                                }
                              </span>
                            )}
                            <span className={`text-sm ${licenseExpired ? 'text-amber-600' : 'text-red-600'}`}>
                              {licenseExpired 
                                ? (language === 'ar' 
                                    ? 'يرجى رفع رخصة تجارية سارية المفعول للمتابعة' 
                                    : 'Please upload a valid trade license to continue')
                                : licenseRejectionReason
                              }
                            </span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={`mt-3 w-full ${licenseExpired ? 'border-amber-400 text-amber-700 hover:bg-amber-100' : 'border-red-300 text-red-600 hover:bg-red-100'}`}
                          onClick={() => {
                            setLicenseRejected(false);
                            setLicenseRejectionReason('');
                            setLicenseExpired(false);
                            setLicenseExpiryDate('');
                            setLicenseFile(null);
                            setLicensePreview(null);
                            licenseInputRef.current?.click();
                          }}
                        >
                          {licenseExpired 
                            ? (language === 'ar' ? 'رفع رخصة سارية' : 'Upload Valid License')
                            : (language === 'ar' ? 'رفع صورة أخرى' : 'Upload Another Image')
                          }
                        </Button>
                      </div>
                    )}

                    {/* License Verified Success */}
                    {licenseData && !licenseRejected && (
                      <div className="mt-3 p-4 bg-green-50 rounded-xl border border-green-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <div className="flex-1">
                            <span className="text-green-700 font-bold block">
                              {language === 'ar' ? 'تم التحقق من الرخصة التجارية ✓' : 'Trade License Verified ✓'}
                            </span>
                            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                              <div>
                                <span className="text-gray-500">{language === 'ar' ? 'الاسم التجاري:' : 'Trade Name:'}</span>
                                <span className="text-gray-800 font-medium ms-1">{licenseData.company_name_ar}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">{language === 'ar' ? 'رقم الرخصة:' : 'License #:'}</span>
                                <span className="text-gray-800 font-medium ms-1">{licenseData.license_number}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Is Owner Question */}
                  <div className={`rounded-xl p-5 border ${
                    isDarkMode 
                      ? 'bg-[#38BDF8]/10 border-[#38BDF8]/30' 
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <Label className={`font-bold mb-3 block ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {language === 'ar' ? 'هل أنت صاحب المؤسسة؟ *' : 'Are you the owner? *'}
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => {
                          setIsOwner(true);
                          setAuthorizationFile(null);
                          setAuthorizationPreview(null);
                        }}
                        className={`py-3 rounded-lg border-2 font-bold transition-all ${
                          isOwner === true
                            ? 'border-green-500 bg-green-500/10 text-green-500'
                            : isDarkMode 
                              ? 'border-white/10 text-gray-300 hover:border-white/20'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {language === 'ar' ? 'نعم' : 'Yes'}
                      </button>
                      <button
                        onClick={() => setIsOwner(false)}
                        className={`py-3 rounded-lg border-2 font-bold transition-all ${
                          isOwner === false
                            ? 'border-red-500 bg-red-500/10 text-red-500'
                            : isDarkMode 
                              ? 'border-white/10 text-gray-300 hover:border-white/20'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {language === 'ar' ? 'لا' : 'No'}
                      </button>
                    </div>
                  </div>

                  {/* Authorization Letter */}
                  {isOwner === false && (
                    <div className="space-y-4">
                      {/* Authorization Letter from Company */}
                      <div className="bg-[#1E9BD9]/10 rounded-xl p-5 border border-[#1E9BD9]">
                        <Label className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                          <FileText className="h-5 w-5 text-[#1E9BD9]" />
                          {language === 'ar' ? 'رسالة التخويل من الشركة *' : 'Authorization Letter from Company *'}
                        </Label>
                        <p className="text-sm text-[#1E9BD9] mb-2">
                          {language === 'ar' ? 'رسالة رسمية من الشركة تُخولك لإدارة المتجر' : 'Official letter from the company authorizing you to manage the store'}
                        </p>
                        <p className="text-xs text-[#1E9BD9] bg-[#1E9BD9]/20 rounded-lg px-3 py-2 mb-3 font-medium">
                          {language === 'ar' 
                            ? '⚠️ ملاحظة: يجب أن تكون رسالة التفويض باسم صاحب الحساب على CloudGM Business' 
                            : '⚠️ Note: Authorization letter must be in the name of the CloudGM Business account holder'}
                        </p>
                        <div 
                          onClick={() => authorizationInputRef.current?.click()}
                          className="w-full h-36 rounded-xl border-2 border-dashed border-[#1E9BD9] flex items-center justify-center cursor-pointer hover:border-[#1E9BD9] transition-colors overflow-hidden bg-white"
                          data-testid="authorization-upload"
                        >
                          {authorizationFile ? (
                            <FilePreview 
                              file={authorizationFile} 
                              preview={authorizationPreview}
                              onRemove={() => { setAuthorizationFile(null); setAuthorizationPreview(null); }}
                            />
                          ) : (
                            <div className="text-center">
                              <Upload className="h-10 w-10 mx-auto text-[#1E9BD9]" />
                              <p className="text-sm text-[#1E9BD9] mt-2 font-medium">
                                {language === 'ar' ? 'اضغط لرفع رسالة التخويل' : 'Click to upload authorization letter'}
                              </p>
                              <p className="text-xs text-[#1E9BD9] mt-1">PNG, JPG, PDF - {language === 'ar' ? '3 ميجابايت كحد أقصى' : '3MB max'}</p>
                            </div>
                          )}
                        </div>
                        <input ref={authorizationInputRef} type="file" accept="image/*,.pdf" onChange={(e) => handleFileUpload(e, 'authorization')} className="hidden" />
                      </div>

                      {/* Signature Approval Letter */}
                      <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                        <Label className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                          <FileText className="h-5 w-5 text-teal-600" />
                          {language === 'ar' ? 'رسالة اعتماد التوقيع *' : 'Signature Approval Letter *'}
                        </Label>
                        <p className="text-sm text-teal-600 mb-3">
                          {language === 'ar' ? 'رسالة تُثبت اعتماد توقيعك من قِبل الشركة' : 'Letter verifying your signature approval by the company'}
                        </p>
                        <div 
                          onClick={() => signatureInputRef.current?.click()}
                          className="w-full h-36 rounded-xl border-2 border-dashed border-teal-300 flex items-center justify-center cursor-pointer hover:border-teal-400 transition-colors overflow-hidden bg-white"
                          data-testid="signature-upload"
                        >
                          {signatureFile ? (
                            <FilePreview 
                              file={signatureFile} 
                              preview={signaturePreview}
                              onRemove={() => { setSignatureFile(null); setSignaturePreview(null); }}
                            />
                          ) : (
                            <div className="text-center">
                              <Upload className="h-10 w-10 mx-auto text-teal-400" />
                              <p className="text-sm text-teal-600 mt-2 font-medium">
                                {language === 'ar' ? 'اضغط لرفع رسالة اعتماد التوقيع' : 'Click to upload signature approval'}
                              </p>
                              <p className="text-xs text-teal-400 mt-1">PNG, JPG, PDF - {language === 'ar' ? '3 ميجابايت كحد أقصى' : '3MB max'}</p>
                            </div>
                          )}
                        </div>
                        <input ref={signatureInputRef} type="file" accept="image/*,.pdf" onChange={(e) => handleFileUpload(e, 'signature')} className="hidden" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Custom Form (for auto/real_estate/plates) OR Store Details (for others) */}
          {currentStep === 3 && needsCustomForm(storeType) && (
            <div className="space-y-6">
              {storeType === 'auto' && (
                <CarStoreForm 
                  onDataChange={(data, images) => {
                    setCustomFormData(data);
                    if (images) setCarImages(images);
                  }}
                  initialData={customFormData}
                />
              )}
              {storeType === 'real_estate' && (
                <RealEstateForm 
                  onDataChange={(data) => setCustomFormData(data)}
                  initialData={customFormData}
                />
              )}
              {(storeType === 'plates' || storeType === 'car_plates') && (
                <PlateDesigner 
                  onDataChange={(data) => setCustomFormData(data)}
                  initialData={customFormData}
                />
              )}
            </div>
          )}

          {/* Step 3 (no custom form) OR Step 4 (with custom form): Store Details */}
          {((currentStep === 3 && !needsCustomForm(storeType)) || (currentStep === 4 && needsCustomForm(storeType))) && (
            <div className="space-y-6">
              {/* Store Logo */}
              <div>
                <Label className={`font-bold mb-3 block ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {language === 'ar' ? 'شعار المتجر' : 'Store Logo'}
                </Label>
                
                {/* Upload Custom Logo */}
                <div className="mb-4">
                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'ارفع شعارك الخاص:' : 'Upload your own logo:'}
                  </p>
                  <div 
                    onClick={() => {
                      // Reset the input value to allow re-selecting the same file
                      if (logoInputRef.current) {
                        logoInputRef.current.value = '';
                      }
                      logoInputRef.current?.click();
                      setSelectedDefaultLogo(null);
                    }}
                    className={`w-32 h-32 mx-auto rounded-2xl border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors overflow-hidden ${
                      logoPreview && !selectedDefaultLogo 
                        ? isDarkMode ? 'border-[#38BDF8] bg-[#38BDF8]/10' : 'border-[#1E9BD9] bg-[#1E9BD9]/5'
                        : isDarkMode ? 'border-white/20 hover:border-[#38BDF8] bg-[#0D1421]' : 'border-gray-300 hover:border-[#1E9BD9] bg-gray-50'
                    }`}
                    data-testid="upload-custom-logo"
                  >
                    {logoPreview && !selectedDefaultLogo ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <Image className={`h-10 w-10 mx-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{language === 'ar' ? 'رفع شعار' : 'Upload'}</p>
                      </div>
                    )}
                  </div>
                  <p className={`text-xs text-center mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    PNG, JPG - {language === 'ar' ? '3 ميجابايت كحد أقصى' : '3MB max'}
                  </p>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={(e) => {
                    handleFileUpload(e, 'logo');
                    setSelectedDefaultLogo(null);
                  }} className="hidden" />
                </div>

                {/* OR Divider */}
                <div className="flex items-center gap-4 my-6">
                  <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}></div>
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {language === 'ar' ? 'أو اختر من الافتراضي' : 'Or choose default'}
                  </span>
                  <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}></div>
                </div>

                {/* Default Logos - Filtered by store type */}
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {getFilteredLogos().map((logo) => (
                    <button
                      key={logo.id}
                      onClick={() => {
                        setSelectedDefaultLogo(logo.id);
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      className={`w-full aspect-square rounded-xl bg-gradient-to-br ${logo.color} flex items-center justify-center text-2xl sm:text-3xl transition-all ${
                        selectedDefaultLogo === logo.id
                          ? 'ring-4 ring-[#1E9BD9] ring-offset-2 scale-105'
                          : 'hover:scale-105 opacity-80 hover:opacity-100'
                      }`}
                      data-testid={`default-logo-${logo.id}`}
                    >
                      {logo.icon}
                    </button>
                  ))}
                </div>

                {selectedDefaultLogo && (
                  <p className="text-center text-sm text-[#1E9BD9] mt-3 font-medium">
                    {language === 'ar' ? '✓ تم اختيار الشعار الافتراضي' : '✓ Default logo selected'}
                  </p>
                )}
              </div>

              {/* Store Name Arabic */}
              <div>
                <Label className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {language === 'ar' ? 'اسم المتجر (عربي) *' : 'Store Name (Arabic) *'}
                  {translatingName && <Loader2 className="h-4 w-4 animate-spin text-[#38BDF8]" />}
                  {businessType === 'company' && licenseData && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {language === 'ar' ? 'من الرخصة التجارية' : 'From Trade License'}
                    </span>
                  )}
                </Label>
                <Input
                  value={storeName}
                  onChange={(e) => {
                    // Don't allow changes if company has license data
                    if (businessType === 'company' && licenseData) return;
                    
                    const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\s0-9.,!?؟،؛:()-]*$/;
                    if (arabicRegex.test(e.target.value) || e.target.value === '') {
                      handleStoreNameChange(e.target.value);
                    }
                  }}
                  placeholder={language === 'ar' ? 'مثال: متجر الذهب' : 'Example: Golden Store'}
                  className={`text-lg py-3 ${
                    businessType === 'company' && licenseData 
                      ? isDarkMode ? 'bg-white/5 cursor-not-allowed' : 'bg-gray-100 cursor-not-allowed'
                      : isDarkMode ? 'bg-[#0D1421] border-white/10 text-white placeholder:text-gray-500' : 'placeholder:text-gray-400'
                  }`}
                  dir="rtl"
                  readOnly={businessType === 'company' && licenseData}
                />
                {businessType === 'company' && licenseData && (
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'الاسم مُستخرج من الرخصة التجارية ولا يمكن تعديله' : 'Name extracted from trade license and cannot be modified'}
                  </p>
                )}
              </div>

              {/* Store Name English */}
              <div>
                <Label className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {language === 'ar' ? 'اسم المتجر (إنجليزي) *' : 'Store Name (English) *'}
                  {translatingName && <Loader2 className="h-4 w-4 animate-spin text-[#38BDF8]" />}
                  {businessType === 'company' && licenseData && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {language === 'ar' ? 'من الرخصة التجارية' : 'From Trade License'}
                    </span>
                  )}
                </Label>
                <Input
                  value={storeNameEn}
                  onChange={(e) => {
                    // Don't allow changes if company has license data
                    if (businessType === 'company' && licenseData) return;
                    
                    const englishRegex = /^[a-zA-Z0-9\s.,!?;:'"()-]*$/;
                    if (englishRegex.test(e.target.value) || e.target.value === '') {
                      handleStoreNameEnChange(e.target.value);
                    }
                  }}
                  placeholder="Example: Golden Store"
                  className={`text-lg py-3 ${
                    businessType === 'company' && licenseData 
                      ? isDarkMode ? 'bg-white/5 cursor-not-allowed' : 'bg-gray-100 cursor-not-allowed'
                      : isDarkMode ? 'bg-[#0D1421] border-white/10 text-white placeholder:text-gray-500' : 'placeholder:text-gray-400'
                  }`}
                  dir="ltr"
                  readOnly={businessType === 'company' && licenseData}
                />
                {businessType === 'company' && licenseData && (
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'الاسم مُستخرج من الرخصة التجارية ولا يمكن تعديله' : 'Name extracted from trade license and cannot be modified'}
                  </p>
                )}
              </div>

              {/* License Expiry Date - Only for companies with license data */}
              {businessType === 'company' && licenseData && (
                <div className={`rounded-xl p-4 border ${
                  isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
                }`}>
                  <Label className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-800'}`}>
                    <FileText className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                    {language === 'ar' ? 'تاريخ انتهاء الرخصة التجارية' : 'Trade License Expiry Date'}
                  </Label>
                  <Input
                    type="date"
                    value={licenseData.expiry_date || ''}
                    readOnly
                    className={`cursor-not-allowed font-bold ${
                      isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-900'
                    }`}
                  />
                  {licenseData.expiry_date && new Date(licenseData.expiry_date) < new Date() && (
                    <p className="text-sm text-red-600 mt-2 font-bold flex items-center gap-1">
                      <X className="h-4 w-4" />
                      {language === 'ar' ? 'تحذير: الرخصة منتهية الصلاحية! يرجى تجديد الرخصة أولاً.' : 'Warning: License has expired! Please renew the license first.'}
                    </p>
                  )}
                  {licenseData.expiry_date && new Date(licenseData.expiry_date) >= new Date() && (
                    <p className="text-xs text-amber-700 mt-2">
                      {language === 'ar' ? 'التاريخ مُستخرج من الرخصة التجارية ولا يمكن تعديله' : 'Date extracted from trade license and cannot be modified'}
                    </p>
                  )}
                </div>
              )}

              {/* AI Description Generator Button */}
              <div className={`rounded-xl p-4 border ${
                isDarkMode 
                  ? 'bg-[#38BDF8]/10 border-[#38BDF8]/30' 
                  : 'bg-gradient-to-r from-sky-50 to-blue-50 border-sky-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      <Languages className={`h-5 w-5 ${isDarkMode ? 'text-[#38BDF8]' : 'text-sky-600'}`} />
                      {language === 'ar' ? 'إنشاء وصف تلقائي' : 'Generate Description'}
                    </h4>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {language === 'ar' 
                        ? 'سيتم إنشاء وصف احترافي بالعربي والإنجليزي بناءً على اسم ونوع المتجر'
                        : 'AI will generate professional description in Arabic & English based on store name and type'
                      }
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={generateDescription}
                    disabled={!storeName || !storeNameEn || generatingDesc}
                    className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-white"
                  >
                    {generatingDesc ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Languages className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'إنشاء' : 'Generate'}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Store Description Arabic */}
              <div>
                <Label className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {language === 'ar' ? 'وصف المتجر (عربي)' : 'Store Description (Arabic)'}
                  {translatingDesc && <Loader2 className="h-4 w-4 animate-spin text-[#38BDF8]" />}
                </Label>
                <textarea
                  value={storeDescription}
                  onChange={(e) => {
                    const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\s0-9.,!?؟،؛:()-]*$/;
                    if (arabicRegex.test(e.target.value) || e.target.value === '') {
                      handleDescriptionChange(e.target.value);
                    }
                  }}
                  placeholder={language === 'ar' ? 'وصف قصير عن متجرك بالعربي...' : 'Brief description in Arabic...'}
                  className={`w-full px-4 py-3 border rounded-lg h-24 focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 resize-none ${
                    isDarkMode 
                      ? 'bg-[#0D1421] border-white/10 text-white placeholder:text-gray-500' 
                      : 'bg-white border-gray-200'
                  }`}
                  dir="rtl"
                />
              </div>

              {/* Store Description English */}
              <div>
                <Label className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {language === 'ar' ? 'وصف المتجر (إنجليزي)' : 'Store Description (English)'}
                  {translatingDesc && <Loader2 className="h-4 w-4 animate-spin text-[#38BDF8]" />}
                </Label>
                <textarea
                  value={storeDescriptionEn}
                  onChange={(e) => {
                    const englishRegex = /^[a-zA-Z0-9\s.,!?;:'"()-]*$/;
                    if (englishRegex.test(e.target.value) || e.target.value === '') {
                      handleDescriptionEnChange(e.target.value);
                    }
                  }}
                  placeholder="Brief description in English..."
                  className={`w-full px-4 py-3 border rounded-lg h-24 focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 resize-none ${
                    isDarkMode 
                      ? 'bg-[#0D1421] border-white/10 text-white placeholder:text-gray-500' 
                      : 'bg-white border-gray-200'
                  }`}
                  dir="ltr"
                />
              </div>
            </div>
          )}

          {/* Step 4 (no custom form) OR Step 5 (with custom form): Review */}
          {((currentStep === 4 && !needsCustomForm(storeType)) || (currentStep === 5 && needsCustomForm(storeType))) && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                  <CheckCircle className={`h-10 w-10 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                </div>
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {language === 'ar' ? 'مراجعة بيانات المتجر' : 'Review Store Details'}
                </h3>
              </div>

              {/* Review Card */}
              <div className={`rounded-xl p-6 space-y-4 ${isDarkMode ? 'bg-[#131B2C]' : 'bg-gray-50'}`}>
                {/* Logo at top */}
                {(logoPreview || selectedDefaultLogo) && (
                  <div className={`flex flex-col items-center pb-4 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <span className={`mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{language === 'ar' ? 'شعار المتجر' : 'Store Logo'}</span>
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-20 h-20 rounded-xl object-cover shadow-md" />
                    ) : selectedDefaultLogo ? (
                      <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${defaultLogos.find(l => l.id === selectedDefaultLogo)?.color} flex items-center justify-center text-4xl shadow-md`}>
                        {defaultLogos.find(l => l.id === selectedDefaultLogo)?.icon}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Store Type */}
                <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{language === 'ar' ? 'نوع المتجر' : 'Store Type'}</span>
                  <div className="flex items-center gap-2">
                    {selectedStoreType && (
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${selectedStoreType.color} flex items-center justify-center`}>
                        {(() => { const Icon = selectedStoreType.icon; return <Icon className="h-4 w-4 text-white" />; })()}
                      </div>
                    )}
                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {language === 'ar' ? selectedStoreType?.name : selectedStoreType?.nameEn}
                    </span>
                  </div>
                </div>

                {/* Business Type */}
                <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{language === 'ar' ? 'نوع النشاط' : 'Business Type'}</span>
                  <span className={`font-bold px-3 py-1 rounded-full text-sm ${
                    businessType === 'personal' 
                      ? isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                      : isDarkMode ? 'bg-[#38BDF8]/20 text-[#38BDF8]' : 'bg-[#1E9BD9]/20 text-[#1E9BD9]'
                  }`}>
                    {businessType === 'personal' 
                      ? (language === 'ar' ? 'تجارة شخصية' : 'Personal') 
                      : (language === 'ar' ? 'شركة' : 'Company')}
                  </span>
                </div>

                {/* Store Name Arabic */}
                <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{language === 'ar' ? 'اسم المتجر (عربي)' : 'Store Name (Arabic)'}</span>
                  <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} dir="rtl">{storeName}</span>
                </div>

                {/* Store Name English */}
                <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{language === 'ar' ? 'اسم المتجر (إنجليزي)' : 'Store Name (English)'}</span>
                  <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} dir="ltr">{storeNameEn}</span>
                </div>

                {/* Store Description Arabic */}
                {storeDescription && (
                  <div className={`py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <span className={`block mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{language === 'ar' ? 'وصف المتجر (عربي)' : 'Store Description (Arabic)'}</span>
                    <p className={`text-sm p-3 rounded-lg border ${isDarkMode ? 'text-white bg-[#0D1421] border-white/10' : 'text-[#0F172A] bg-white border-gray-200'}`} dir="rtl">{storeDescription}</p>
                  </div>
                )}

                {/* Store Description English */}
                {storeDescriptionEn && (
                  <div className={`py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <span className={`block mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{language === 'ar' ? 'وصف المتجر (إنجليزي)' : 'Store Description (English)'}</span>
                    <p className={`text-sm p-3 rounded-lg border ${isDarkMode ? 'text-white bg-[#0D1421] border-white/10' : 'text-[#0F172A] bg-white border-gray-200'}`} dir="ltr">{storeDescriptionEn}</p>
                  </div>
                )}

                {/* Documents Status - Company Only */}
                {businessType === 'company' && (
                  <>
                    <div className="pt-2">
                      <span className={`font-medium block mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{language === 'ar' ? 'المستندات المرفقة' : 'Attached Documents'}</span>
                    </div>
                    
                    <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{language === 'ar' ? 'الرخصة التجارية' : 'Commercial License'}</span>
                      <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDarkMode ? 'text-green-400 bg-green-500/20' : 'text-green-600 bg-green-50'}`}>
                        <CheckCircle className="h-4 w-4" />
                        {language === 'ar' ? 'تم الرفع' : 'Uploaded'}
                      </span>
                    </div>
                    
                    <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{language === 'ar' ? 'صاحب المؤسسة' : 'Owner Status'}</span>
                      <span className={`font-bold px-3 py-1 rounded-full text-sm ${
                        isOwner 
                          ? isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                          : isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {isOwner ? (language === 'ar' ? 'نعم - المالك' : 'Yes - Owner') : (language === 'ar' ? 'لا - مُفوض' : 'No - Authorized')}
                      </span>
                    </div>
                    
                    {isOwner === false && (
                      <>
                        <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{language === 'ar' ? 'رسالة التخويل' : 'Authorization Letter'}</span>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDarkMode ? 'text-green-400 bg-green-500/20' : 'text-green-600 bg-green-50'}`}>
                            <CheckCircle className="h-4 w-4" />
                            {language === 'ar' ? 'تم الرفع' : 'Uploaded'}
                          </span>
                        </div>
                        <div className={`flex items-center justify-between py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{language === 'ar' ? 'رسالة اعتماد التوقيع' : 'Signature Approval'}</span>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${isDarkMode ? 'text-green-400 bg-green-500/20' : 'text-green-600 bg-green-50'}`}>
                            <CheckCircle className="h-4 w-4" />
                            {language === 'ar' ? 'تم الرفع' : 'Uploaded'}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Ad Preview Section for Auto and Real Estate */}
              {needsCustomForm(storeType) && (storeType === 'auto' || storeType === 'real_estate') && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Eye className="h-5 w-5 text-amber-500" />
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {language === 'ar' ? 'معاينة الإعلان' : 'Ad Preview'}
                    </h3>
                  </div>
                  
                  {storeType === 'auto' && (
                    <CarAdPreview 
                      data={customFormData}
                      images={carImages}
                      storeName={storeName}
                    />
                  )}
                  
                  {storeType === 'real_estate' && (
                    <RealEstateAdPreview 
                      data={customFormData}
                      images={[]}
                      storeName={storeName}
                    />
                  )}
                </div>
              )}

              {/* Notice */}
              <div className={`rounded-xl p-4 border ${isDarkMode ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
                <p className={`text-sm text-center ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}>
                  {language === 'ar' 
                    ? 'سيتم مراجعة متجرك من قبل الإدارة وإشعارك بالموافقة خلال 48 ساعة كحد أقصى.'
                    : 'Your store will be reviewed by admin. You will be notified within 48 hours maximum.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {currentStep > 1 && (
            <Button
              onClick={prevStep}
              variant="outline"
              className={`flex-1 py-6 text-lg ${isDarkMode ? 'border-[#334155] text-gray-300 hover:bg-[#334155]' : ''}`}
            >
              <ArrowRight className="h-5 w-5 me-2" />
              {language === 'ar' ? 'السابق' : 'Previous'}
            </Button>
          )}
          
          {currentStep < getTotalSteps() ? (
            <Button
              onClick={nextStep}
              disabled={!canProceed()}
              className="flex-1 py-6 text-lg bg-[#1E9BD9] hover:bg-[#0369A1] disabled:opacity-50"
            >
              {language === 'ar' ? 'التالي' : 'Next'}
              <ArrowLeft className="h-5 w-5 ms-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={creating}
              className="flex-1 py-6 text-lg bg-green-600 hover:bg-green-700"
            >
              {creating ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <Check className="h-5 w-5 me-2" />
                  {language === 'ar' ? 'إنشاء المتجر' : 'Create Store'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Image Cropper Modal */}
      {showCropper && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={cancelCrop}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Scissors className="h-5 w-5 text-[#1E9BD9]" />
                {language === 'ar' ? 'تحرير الشعار' : 'Edit Logo'}
              </h3>
              <button onClick={cancelCrop} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-2 text-center text-sm text-gray-500 bg-blue-50">
              {language === 'ar' ? 'اسحب لتحديد منطقة القص أو اضغط تطبيق للاستخدام المباشر' : 'Drag to select crop area or click Apply to use directly'}
            </div>
            
            <div className="p-4 flex items-center justify-center bg-gray-100" style={{ minHeight: '300px', maxHeight: '55vh', overflow: 'auto' }}>
              {cropImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                >
                  <img 
                    ref={cropImageRef}
                    src={cropImage} 
                    alt="Crop" 
                    style={{ maxHeight: '50vh', maxWidth: '100%' }}
                    onLoad={(e) => {
                      const { width, height } = e.currentTarget;
                      const size = Math.min(width, height) * 0.8;
                      const x = (width - size) / 2;
                      const y = (height - size) / 2;
                      setCrop({ unit: 'px', width: size, height: size, x, y });
                    }}
                  />
                </ReactCrop>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <Button
                onClick={cancelCrop}
                variant="outline"
                className="flex-1"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={getCroppedImg}
                className="flex-1 bg-[#1E9BD9] hover:bg-[#0369A1]"
              >
                <Check className="h-4 w-4 me-2" />
                {language === 'ar' ? 'تطبيق' : 'Apply'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
